import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Satisfaction Claims — admin queue of OPEN / UNDER_REVIEW satisfaction-guarantee
// claims on STANDARD (on-demand / scheduled) bookings, with case-by-case resolution.
//
// Cloned from the Big Projects · Disputes admin page, but for BookingClaim and
// NOT gated by largeProjectsEnabled. Consumes besthand-api `listBookingClaims`
// through the `/lp-graphql` proxy via `lpApi`; resolves with `resolveBookingClaim`
// (admin-gated server-side via requireAdmin). The payout source is CASE-BY-CASE:
// the admin picks Refund (platform), Clawback (pro), Credit (wallet), Re-do, or Deny.
// ============================================================================

const Q_CLAIMS = `
  query {
    listBookingClaims {
      status errorMessage
      claims {
        id bookingId claimantUserId reasonCode description status
        resolutionType refundAmount resolvedBy adminNotes createdAt
      }
    }
  }
`;

// resolveBookingClaim(claimId!, resolutionType!, refundAmount, adminNotes)
// resolutionType ∈ REFUND_PLATFORM | CLAWBACK_PRO | REDO | CREDIT | DENIED
const M_RESOLVE = `
  mutation ($claimId: Int!, $resolutionType: String!, $refundAmount: Float, $adminNotes: String) {
    resolveBookingClaim(claimId: $claimId, resolutionType: $resolutionType, refundAmount: $refundAmount, adminNotes: $adminNotes) {
      status errorMessage
      claim { id status resolutionType refundAmount }
    }
  }
`;

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '18px 20px', marginBottom: 14, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  desc: { fontSize: 14, color: '#0f1214', marginTop: 10, lineHeight: 1.5 },
  badge: (kind) => {
    const map = {
      OPEN: { bg: '#fff3d6', fg: '#946200' },
      UNDER_REVIEW: { bg: '#eef2ff', fg: '#3730a3' },
      RESOLVED: { bg: '#e7f6ec', fg: '#137a37' },
      DENIED: { bg: '#fde7e8', fg: '#c2151c' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnBlue: { background: '#fff', color: '#1d4ed8', border: '1px solid #c7d2fe', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnAmber: { background: '#fff', color: '#946200', border: '1px solid #fde68a', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnGhostSm: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnRed: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const REASON_LABEL = {
  WORK_NOT_AS_AGREED: 'Work not as agreed',
  INCOMPLETE: 'Incomplete',
  QUALITY: 'Quality',
  DELAY: 'Delay',
  UNAUTHORIZED_CHARGE: 'Unauthorized charge',
  NOT_PERFORMED: 'Not performed',
  OTHER: 'Other',
};

const fmtMoney = (v) => (v == null ? null : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function Claims() {
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_CLAIMS);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listBookingClaims;
        if (env && env.status === 200) setClaims(env.claims || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load claims: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, query, variables, opName, okText) => {
    setBusyId(key);
    setMsg(null);
    try {
      const res = await lpApi(query, variables);
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data[opName];
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: okText });
        await load();
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  const isOpen = (c) => c.status === 'OPEN' || c.status === 'UNDER_REVIEW';

  // resolutionType ∈ REFUND_PLATFORM | CLAWBACK_PRO | CREDIT | REDO | DENIED
  const resolve = (c, resolutionType) => {
    const moneyBranch = ['REFUND_PLATFORM', 'CLAWBACK_PRO', 'CREDIT'].includes(resolutionType);
    const labels = {
      REFUND_PLATFORM: 'a platform refund to the client',
      CLAWBACK_PRO: 'a clawback from the worker',
      CREDIT: 'a wallet credit to the client',
      REDO: 'a re-do (no money moves)',
      DENIED: 'a denial (no money moves)',
    };
    let refundAmount = null;
    if (moneyBranch) {
      const raw = typeof window !== 'undefined'
        ? window.prompt(`Claim #${c.id} → ${labels[resolutionType]}.\n\nAmount (USD):`) : '';
      if (raw === null) return;
      refundAmount = Number(raw);
      if (!Number.isFinite(refundAmount) || refundAmount <= 0) {
        setMsg({ error: 'Amount must be a number greater than 0.' });
        return;
      }
    }
    const adminNotes = typeof window !== 'undefined'
      ? window.prompt(`Resolving claim #${c.id} with ${labels[resolutionType]}.\n\nOptional admin note (leave blank to skip):`) : '';
    if (adminNotes === null) return; // admin cancelled
    run(
      `c${c.id}`,
      M_RESOLVE,
      { claimId: Number(c.id), resolutionType, refundAmount, adminNotes: adminNotes.trim() ? adminNotes.trim() : null },
      'resolveBookingClaim',
      `Claim #${c.id} resolved (${resolutionType.toLowerCase()}).`,
    );
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Satisfaction Claims</h2>
      <div style={S.hint}>
        Open and under-review satisfaction-guarantee claims on standard bookings, oldest first.
        The payout source is case-by-case — choose Refund (platform absorbs), Clawback (recoup from
        the worker), Credit (client wallet), Re-do, or Deny.
      </div>

      <div style={S.toolbar}>
        <button style={S.btnGhost} onClick={() => load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{claims.length} open</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        claims.length === 0 ? (
          <div style={S.empty}>No open claims. 🎉</div>
        ) : claims.map((c) => (
          <div style={S.card} key={c.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>Claim #{c.id} · {REASON_LABEL[c.reasonCode] || c.reasonCode}</p>
                <div style={S.meta}>
                  Booking #{c.bookingId}
                  {c.claimantUserId ? ` · Opened by ${c.claimantUserId}` : ''}
                  {c.createdAt ? ` · ${fmtDate(c.createdAt)}` : ''}
                  {c.refundAmount != null ? ` · ${fmtMoney(c.refundAmount)}` : ''}
                </div>
                {c.description ? <div style={S.desc}>{c.description}</div> : null}
                {c.adminNotes ? <div style={S.meta}>Note: {c.adminNotes}</div> : null}
              </div>
              <span style={S.badge(c.status)}>{c.status || 'OPEN'}</span>
            </div>
            {isOpen(c) && (
              <div style={S.actions}>
                <button style={S.btn} disabled={busyId === `c${c.id}`} onClick={() => resolve(c, 'REFUND_PLATFORM')}>
                  {busyId === `c${c.id}` ? '…' : 'Refund · Platform'}
                </button>
                <button style={S.btnAmber} disabled={busyId === `c${c.id}`} onClick={() => resolve(c, 'CLAWBACK_PRO')}>
                  Clawback · Pro
                </button>
                <button style={S.btnBlue} disabled={busyId === `c${c.id}`} onClick={() => resolve(c, 'CREDIT')}>
                  Credit · Wallet
                </button>
                <button style={S.btnGhostSm} disabled={busyId === `c${c.id}`} onClick={() => resolve(c, 'REDO')}>
                  Re-do
                </button>
                <button style={S.btnRed} disabled={busyId === `c${c.id}`} onClick={() => resolve(c, 'DENIED')}>
                  Deny
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default Claims;
